/**
 * First-run question (W2-14 onboarding-wizard; DESIGN.md §4.2; `DIALOG.firstRun`): shown once on the first launch
 * (the Library asks it) and again from Help ("Show the welcome question again").
 *
 *  ( ) I'm new to charting          Guided level · plain hints · 2-minute tour
 *  ( ) I've used RPE (Re:PhiEdit)   Full level · RPE keyboard · "For RPE users" guide
 *  ( ) I've used other editors      Full level · Default keyboard
 *  Language [English | 简体中文]     (from the browser on the first launch)
 *
 * The language switches the UI at once, so the rest of the question is read in it; dismissing the dialog puts the
 * previous language back. Continue resolves `{ level, preset, locale }`; nothing defaults to Full for newcomers.
 *
 * The RPE choice promises the "For RPE users" guide: after Continue with it (unless RPE was already the answer),
 * the Help panel opens at that article, once the opener has applied the answer (Library on the first launch,
 * `help.welcome` later), so its keyboard switch already shows the RPE preset.
 */

import { useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { DIALOG } from '../../app/dialogs/types';
import type { AppServices, DialogProps } from '../../app/registry/types';
import type { Locale } from '../../core/util/types';
import { Button, Dialog, Icon, Segmented } from '../../ui';
import { answerOf, choiceOf, FIRST_RUN_CHOICES, LOCALES, type FirstRunAnswer, type FirstRunChoice } from './firstRun';
import { TOPIC } from './helpTopics';
import { useServicesText } from './hooks';
import { m } from './onboarding.messages';
import s from './Onboarding.module.css';

const CHOICE_TEXT: Readonly<
  Record<FirstRunChoice, { readonly title: string; readonly body: string; readonly icon: string }>
> = {
  new: { title: m.frNew, body: m.frNewBody, icon: 'GraduationCap' },
  rpe: { title: m.frRpe, body: m.frRpeBody, icon: 'Keyboard' },
  other: { title: m.frOther, body: m.frOtherBody, icon: 'Compass' },
};

const LOCALE_TEXT: Readonly<Record<Locale, string>> = { en: m.langEn, 'zh-CN': m.langZh };

/**
 * Opens the "For RPE users" article after the dialog closed: a macrotask later, so the opener has applied the
 * answer (it does so right after its `openDialog` resolves) and this dialog is gone.
 */
export function openRpeGuide(services: Pick<AppServices, 'ui'>): void {
  window.setTimeout(() => {
    void services.ui.openDialog(DIALOG.help, { topic: TOPIC.rpeUsers });
  }, 0);
}

export function FirstRunDialog(p: DialogProps<'firstRun'>) {
  const { services } = p;
  const text = useServicesText(services);
  const [original] = useState<Locale>(() => services.i18n.locale);
  const [previous] = useState<FirstRunChoice | null>(() => {
    const g = services.settings.value;
    return g.general.firstRunDone ? choiceOf(g.general.level, g.keyboard.preset) : null;
  });
  const [choice, setChoice] = useState<FirstRunChoice>(previous ?? 'new');
  const [locale, setLocale] = useState<Locale>(original);
  const refs = useRef(new Map<FirstRunChoice, HTMLButtonElement>());
  const done = useRef(false);

  const finish = (answer: FirstRunAnswer | null): void => {
    if (done.current) return;
    done.current = true;
    if (answer === null && services.i18n.locale !== original) services.i18n.setLocale(original);
    p.close(answer);
    if (answer !== null && choiceOf(answer.level, answer.preset) === 'rpe' && previous !== 'rpe') {
      openRpeGuide(services);
    }
  };

  const pickLocale = (l: Locale): void => {
    setLocale(l);
    if (services.i18n.locale !== l) services.i18n.setLocale(l);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    e.preventDefault();
    const i = FIRST_RUN_CHOICES.indexOf(choice);
    const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const next = FIRST_RUN_CHOICES[(i + (forward ? 1 : -1) + FIRST_RUN_CHOICES.length) % FIRST_RUN_CHOICES.length];
    if (next === undefined) return;
    setChoice(next);
    refs.current.get(next)?.focus();
  };

  return (
    <Dialog
      open
      size="md"
      title={text.t(m.frTitle)}
      description={text.t(m.frIntro)}
      onClose={() => {
        finish(null);
      }}
      footer={
        <div className={s.dialogFooter}>
          <span className={s.muted}>{text.t(m.frLater)}</span>
          <Button
            variant="primary"
            icon="ArrowRight"
            onClick={() => {
              finish(answerOf(choice, locale));
            }}
          >
            {text.t(m.frContinue)}
          </Button>
        </div>
      }
    >
      <div className={s.choices} role="radiogroup" aria-label={text.t(m.frChoicesLabel)} onKeyDown={onKeyDown}>
        {FIRST_RUN_CHOICES.map((c) => {
          const checked = c === choice;
          const t = CHOICE_TEXT[c];
          return (
            <button
              key={c}
              ref={(el) => {
                if (el === null) refs.current.delete(c);
                else refs.current.set(c, el);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              className={s.choice}
              data-checked={checked ? 'true' : undefined}
              data-autofocus={checked ? '' : undefined}
              onClick={() => {
                setChoice(c);
              }}
            >
              <span className={s.choiceIcon} aria-hidden="true">
                <Icon name={t.icon} size={20} />
              </span>
              <span className={s.choiceText}>
                <span className={s.choiceTitle}>{text.t(t.title)}</span>
                <span className={s.choiceBody}>{text.t(t.body)}</span>
              </span>
              <span className={s.choiceMark} aria-hidden="true">
                <Icon name={checked ? 'CircleCheck' : 'Circle'} size={18} />
              </span>
            </button>
          );
        })}
      </div>
      <div className={s.languageRow}>
        {/* The icon belongs to the label, on its line (UX-076: it sat alone below "Language"). */}
        <Segmented<Locale>
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <Icon name="Languages" size={14} />
              {text.t(m.frLanguage)}
            </span>
          }
          value={locale}
          options={LOCALES.map((l) => ({ value: l, label: text.t(LOCALE_TEXT[l]) }))}
          onChange={pickLocale}
        />
      </div>
    </Dialog>
  );
}
